import React, { CSSProperties, useState, forwardRef, createRef, useImperativeHandle, useCallback, useEffect } from "react";
import PaymentForm, { ServiceCollection, TokenizerService, ApplicationService, PaymentMethod, generateEmptyRenderFormStyles, PaymentFormRef, PaymentFormLoadingContentContainer, PaymentFormLoadingFailedContentContainer } from "@clarkinc/payment-client";
import { cls } from "@wss/cls";
import { Icon } from "@clarkinc/zest-design-system/ui";
import { PaymentMethodDetails } from "../Models";
import { PaymentSelectionRef, tokenize } from "./PaymentSelectionRefs";
import Environment from "~/common/Environment";
import SavedCardDropdown from "./SavedCardDropdown";
import spritePath from "~/common/Icons/Zest/remixicon.symbol.v1.svg";
interface Props {
  defaultPaymentMethod: PaymentMethod;
  defaultTokenId?: number;
  defaultSavedCards: PaymentMethodDetails[];
  setSavedCardId: (val: number | undefined) => void;
}
const getPaymentMethodToUse = (useSavedCard: boolean) => useSavedCard ? PaymentMethod.BasicCard : PaymentMethod.SavedCard;
const PaymentSelection = forwardRef<PaymentSelectionRef, Props>(({
  defaultPaymentMethod,
  defaultTokenId,
  defaultSavedCards,
  setSavedCardId
}: Props, ref) => {
  const hasSavedCards = defaultSavedCards.length > 0;
  const [paymentMethod, setPaymentMethod] = useState(hasSavedCards ? defaultPaymentMethod : PaymentMethod.BasicCard);
  const [tokenId, setTokenId] = useState(defaultTokenId);
  const [savedCards, setSavedCards] = useState<PaymentMethodDetails[]>(defaultSavedCards);
  const [renderIframe, setRenderIframe] = useState(true);
  const useSavedCard = paymentMethod === PaymentMethod.SavedCard;
  const toggleForm = () => {
    setPaymentMethod(getPaymentMethodToUse(useSavedCard));
    setRenderIframe(useSavedCard);
    return false;
  };
  const serviceCollection = new ServiceCollection(new TokenizerService(`https://tokenizer.${Environment.domainWithEnvironment}`), new ApplicationService(`${window.location.origin}/myaccount:usercard/createrenderformtoken/`, `${window.location.origin}/myaccount:usercard/createtokenizecardtoken/`, `${window.location.origin}/myaccount:usercard/validatetokenizecardresulttoken/`));
  const contentStyles = generateEmptyRenderFormStyles();
  const defaultSelectedSupportedMethod = PaymentMethod.BasicCard;
  const supportedMethods = [defaultSelectedSupportedMethod];
  const toggleLabel = useSavedCard ? "Add a new card" : "Use a saved card";
  const paymentFormRef = createRef<PaymentFormRef>();
  const updateSelectedCard = useCallback((newCard: PaymentMethodDetails) => {
    const existingCard = savedCards.find(x => x.id === newCard.id);
    if (existingCard !== undefined) {
      existingCard.isExpired = newCard.isExpired;
      existingCard.name = newCard.name;
    } else {
      // @ts-expect-error refactor so we don't mutate
      savedCards.push(newCard);
    }
    setSavedCards([...savedCards]);
    setTokenId(newCard.id);
    setSavedCardId(newCard.id);
  }, [savedCards, setSavedCardId]);
  const useableTokenId = useSavedCard ? tokenId : undefined;
  useImperativeHandle(ref, () => ({
    tokenize: tokenize(paymentFormRef, updateSelectedCard, useableTokenId),
    showIframe: () => setRenderIframe(true),
    hideIframe: () => setRenderIframe(false)
  }), [paymentFormRef, updateSelectedCard, useableTokenId]);
  const savedCardStyles: CSSProperties = {};
  const newCardAlertStyles: CSSProperties = {};
  const newCardIframeStyles: CSSProperties = {
    border: "none",
    width: "100%",
    height: "100px"
  };
  if (useSavedCard) {
    newCardIframeStyles.display = "none";
    newCardAlertStyles.display = "none";
  } else {
    savedCardStyles.display = "none";
  }
  useEffect(() => {
    if (savedCards.length === 0 && defaultSavedCards.length > 0) {
      setSavedCards(defaultSavedCards);
      setPaymentMethod(getPaymentMethodToUse(useSavedCard));
      setRenderIframe(false);
    }
  }, [savedCards, defaultSavedCards, useSavedCard]);
  return <>
        <p className="pb-2 m-0 text-xs font-normal text-gray-800">
          Your payment method for your recurring monthly subscription.
        </p>
        {hasSavedCards && savedCards.length > 0 && <SavedCardDropdown isDisabled={false} tokenId={tokenId} savedCards={savedCards} updateSelectedCard={updateSelectedCard} styles={savedCardStyles} />}
        {renderIframe && <PaymentForm title="Payment Form" supportedMethods={supportedMethods} defaultSelectedSupportedMethod={defaultSelectedSupportedMethod} setNewCardAsDefault={false} serviceCollection={serviceCollection} containerStyle={newCardIframeStyles} contentStyles={contentStyles} ref={paymentFormRef}>
            <PaymentFormLoadingContentContainer>
              <div style={newCardAlertStyles} className="pb-0">
                <div className="flex flex-wrap py-1 animate-pulse">
                  <div className="h-10 mb-2 bg-gray-200 rounded w-full" />
                  <div className="h-10 mb-2 bg-gray-200 rounded w-full" />
                  <div className="flex w-full -mr-2">
                    <div className="h-10 mb-2 bg-gray-200 rounded w-1/3" />
                    <div className="h-10 mb-2 ml-2 bg-gray-200 rounded w-1/3" />
                    <div className="h-10 mb-2 ml-2 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              </div>
            </PaymentFormLoadingContentContainer>
            <PaymentFormLoadingFailedContentContainer>
              <div style={newCardAlertStyles}>
                <p className="p-2 text-sm text-center bg-red-200 border border-red-300 border-solid rounded text-red-900">
                  Unable to load credit card form
                </p>
              </div>
            </PaymentFormLoadingFailedContentContainer>
          </PaymentForm>}
        {hasSavedCards && savedCards.length > 0 && <button type="button" className={cls("cursor-pointer", "text-sm", "border-none", "bg-transparent", "text-green-500", "pb-5", "pl-0", "font-semibold", useSavedCard && "pt-4")} onClick={toggleForm}>
            <Icon name="ri-add-line" size="xsmall" spriteSheetHref={spritePath} className="fill-green-500 align-middle" />
            <span className="align-middle">{toggleLabel}</span>
          </button>}
      </>;
});
export default PaymentSelection;